import { useEffect, useRef } from 'react';
import {
  calculateBlockedSpots,
  assignSpotsToZones,
} from '../RampCanvas/spotHelpers';
import { useLocalRampState } from '../RampEditorPresenter';

/**
 * Automatically calculates egress (blocking relationships) whenever shapes change.
 * Uses debouncing to handle rapid changes like dragging.
 *
 * Subscribes directly to the Zustand store to ensure it sees all shape changes.
 */
export function useAutoEgress(debounceMs: number = 300) {
  const lastGeometryHash = useRef<string>('');
  const isFirstRun = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribe directly to the Zustand store
  const shapes = useLocalRampState((state) => state.ramp?.shapes);
  const setRamp = useLocalRampState((state) => state.setRamp);

  useEffect(() => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!shapes || shapes.length === 0) {
      return;
    }

    const currentHash = getShapesGeometryHash(shapes);

    // Skip if hash hasn't changed
    if (currentHash === lastGeometryHash.current) {
      return;
    }

    // Skip first run (data may already be calculated)
    if (isFirstRun.current) {
      isFirstRun.current = false;
      lastGeometryHash.current = currentHash;
      return;
    }

    // Debounce the calculation
    timeoutRef.current = setTimeout(() => {
      lastGeometryHash.current = currentHash;

      // Assign spots to zones first, then calculate egress
      const shapesWithZones = assignSpotsToZones(shapes);
      const result = calculateBlockedSpots(shapesWithZones);

      // Update shapes with egress data
      setRamp((prev) => (prev ? { ...prev, shapes: result.shapes } : prev));
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [shapes, setRamp, debounceMs]);

  // Reset state when shapes become undefined (e.g., ramp switch)
  useEffect(() => {
    if (!shapes) {
      lastGeometryHash.current = '';
      isFirstRun.current = true;
    }
  }, [shapes]);
}

/**
 * Create a hash of shape geometry that affects egress calculations.
 * Excludes calculated fields (egressOptions) to prevent infinite loops.
 */
function getShapesGeometryHash(shapes: { id: string; type: string; points: unknown; tags?: string[]; metadata?: unknown }[]): string {
  const relevantData = shapes.map((s) => ({
    id: s.id,
    type: s.type,
    points: s.points,
    // Include tags to detect egress lane shapes
    isEgressLane: s.tags?.includes('egress_lane'),
    // Include egress lane assignments (user-defined, not calculated)
    egressLanes:
      s.type === 'Spot' ? (s.metadata as { egressLanes?: string[] })?.egressLanes : undefined,
  }));
  return JSON.stringify(relevantData);
}
